import * as _ from 'lodash';

import type { LDAPStrategy } from '../../../../../../types';

export type ValidationState = 'success' | 'warning' | 'error' | null;

// Fields that are always required.
const requiredFields: Readonly<Array<keyof LDAPStrategy>> = Object.freeze([
  'server',
  'port',
  'ssl',
  'username',
  'password',
  'searchBase',
  'searchFilter',
  'userEmailAttr',
  'useGroups',
]);

// Fields that are required to synchronize groups.
const requiredGroupFields: Readonly<Array<keyof LDAPStrategy>> = Object.freeze([
  'groupBase',
  'groupNameAttr',
  'groupDNAttr',
  'groupFilter',
]);

// Field represented by the key of the object is required when the field represented by the value is provided.
const dependentFields = Object.freeze({
  uidAttr: 'allowEmptyEmail',
});

export const defaultLDAPConfig: Readonly<Omit<LDAPStrategy, 'id' | 'organizationId'>> = Object.freeze({
  enabled: false,
  server: '',
  port: 636,
  ssl: true,
  username: '',
  passwordIsSet: false,
  searchBase: '',
  searchFilter: '',
  useGroups: false,
  allowEmptyEmail: false,
  userEmailAttr: '',
});

export const fieldIsEmpty = (ldap: LDAPStrategy, field: keyof LDAPStrategy) => {
  const value = ldap[field];
  return value == null || (_.isString(value) && value.trim() === '');
};

const dependentFieldIsRequired = (ldap: LDAPStrategy, field: keyof LDAPStrategy) =>
  // First, check whether the field depends on another field.
  dependentFields[field] &&
  // Then check if the field that controls when some other field is required is itself set (not empty and not false).
  !fieldIsEmpty(dependentFields[field]) &&
  ldap[dependentFields[field]] !== false;

export const fieldIsRequiredForGroups = (ldap: LDAPStrategy, field: keyof LDAPStrategy) =>
  ldap.useGroups && requiredGroupFields.includes(field);

const fieldIsRequired = (ldap: LDAPStrategy, field: keyof LDAPStrategy) => {
  if (field === 'password' && ldap.passwordIsSet) {
    return false;
  }

  return requiredFields.includes(field) ||
    dependentFieldIsRequired(ldap, field) ||
    fieldIsRequiredForGroups(ldap, field);
};

export const validationState = (ldap: LDAPStrategy, field: keyof LDAPStrategy, canSave: boolean): ValidationState => {
  // If the user hasn't modified any fields yet, don't show any validation
  // errors. This prevents the user from seeing a sea of red on first load.
  if (!canSave) {
    return null;
  }

  if (fieldIsRequired(ldap, field) && fieldIsEmpty(ldap, field)) {
    return 'error';
  }

  return null;
};

export const isLdapConfigurationValid = (ldap: LDAPStrategy, canSave: boolean) => {
  let allRequiredFields: Array<keyof LDAPStrategy> = [...requiredFields, ...Object.keys(dependentFields)];
  if (ldap.useGroups) {
    allRequiredFields = allRequiredFields.concat(requiredGroupFields);
  }

  return allRequiredFields.every(field => validationState(ldap, field, canSave) === null);
};
